import { CanceledError } from 'axios';
import { ImageBlobReduce } from 'image-blob-reduce';
import { messageMediaClient } from '~/services';
import { ValidationError } from '~/utils/errors';
import { isHeic } from '~/utils/files';
import { ALLOWED_FILE_TYPES, ALLOWED_IMAGE_TYPES } from '~/utils/message';
import { ImageResizeError, resizeImage, type ImageEncoder } from './resizeImage';

interface MediaRestrictions {
    blockImages: boolean;
    blockFiles: boolean;
}

type UploadStage = 'converting' | 'resizing' | 'uploading';

interface UploadOwner {
    cancelKey: symbol;
    isCurrent: () => boolean;
}

interface UploadedMedia {
    url: string;
    contentType: string;
    warning: string;
}

const EXTENSION_TYPES: Record<string, string> = {
    csv: 'text/csv',
    gif: 'image/gif',
    jpeg: 'image/jpeg',
    jpg: 'image/jpeg',
    mp3: 'audio/mpeg',
    mp4: 'video/mp4',
    mpga: 'audio/mpeg',
    pdf: 'application/pdf',
    png: 'image/png',
    vcard: 'text/x-vcard',
    vcf: 'text/x-vcard',
};

const RESIZABLE_TYPES = ['image/heic', 'image/jpeg', 'image/png'];

const sizeInKiB = (file: File) => file.size / 1024;

const assertFileTypeAllowed = (type: string, { blockImages, blockFiles }: MediaRestrictions) => {
    if (blockImages && !blockFiles && !ALLOWED_FILE_TYPES.includes(type)) {
        throw new ValidationError(
            'Corporate only allows you to send files in the following formats: mp4, mp3, csv, pdf, vcard.'
        );
    }

    if (!blockImages && blockFiles && !ALLOWED_IMAGE_TYPES.includes(type)) {
        throw new ValidationError(
            'Corporate only allows you to send files in the following formats: gif, jpg, jpeg, png, heic.'
        );
    }
};

const imageDimensions = (file: File): Promise<[number, number]> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => {
                resolve([img.width, img.height]);
            };
            img.onerror = (error) => {
                reject(error);
            };
            img.src = String(reader.result);
        };
        reader.onerror = (error) => {
            reject(error);
        };
        reader.readAsDataURL(file);
    });
};

const halveImageDimension = async (file: File): Promise<File> => {
    const [width] = await imageDimensions(file);

    const resizedWidth = width / 2;
    const reducer = new ImageBlobReduce();
    const result = await reducer.toBlob(file, { max: resizedWidth });

    return new File([result], file.name, {
        type: file.type,
    });
};

const reduceImageQuality = async (file: File, quality: number): Promise<File> => {
    if (quality && (quality > 1 || quality < 0.1)) {
        throw new Error(`reduceImageQuality(): quality should be between 0.1-1.0. Provided: ${quality}`);
    }
    const reducer = new ImageBlobReduce();

    reducer._create_blob = (env) =>
        reducer.pica.toBlob(env.out_canvas!, file.type, quality).then((blob) => {
            env.out_blob = blob;
            return env;
        });
    const result = await reducer.toBlob(file);

    return new File([result], file.name, {
        type: file.type,
    });
};

const imageEncoder: ImageEncoder = {
    reduceQuality: reduceImageQuality,
    halve: halveImageDimension,
};

class HeicConversionError extends Error {
    constructor(cause: unknown) {
        super('Could not convert the HEIC image to JPEG.', { cause });
    }
}

const convertHeicToJpeg = async (file: File): Promise<File> => {
    const { heicTo } = await import('heic-to');
    let blob: Blob;

    try {
        blob = await heicTo({
            blob: file,
            type: 'image/jpeg',
        });
    } catch (e) {
        throw new HeicConversionError(e);
    }

    return new File([blob], `${file.name.replace(/\.[^.]+$/, '')}.jpeg`, { type: 'image/jpeg' });
};

/**
 * Browsers without a HEIF codec report an empty or image/heif type for HEIC files, and Chrome reports none for a vCard.
 */
const detectFileType = async (file: File): Promise<string | undefined> => {
    if (await isHeic(file).catch(() => false)) {
        return 'image/heic';
    }

    if ([...ALLOWED_IMAGE_TYPES, ...ALLOWED_FILE_TYPES].includes(file.type)) {
        return file.type;
    }

    const extension = /\.([^.]+)$/.exec(file.name)?.[1];

    return undefined === extension ? undefined : EXTENSION_TYPES[extension.toLowerCase()];
};

const preparationFailureMessage = (e: unknown): string => {
    if (e instanceof HeicConversionError) {
        return 'The HEIC image could not be converted to JPEG. Please upload it as a JPEG or PNG instead.';
    }
    if (e instanceof ImageResizeError) {
        return 'The image could not be resized to under 500KiB. Please upload a smaller image.';
    }
    if (e instanceof Error && e.message.toLowerCase().includes('fingerprinting protection')) {
        return 'It was not possible to resize the image due to fingerprinting protection. Upload an image smaller than 500KiB.';
    }

    return 'The file failed to upload. Please try again.';
};

export const mayNotBeDelivered = (file: File): boolean => !RESIZABLE_TYPES.includes(file.type) && sizeInKiB(file) > 500;

export const acceptMedia = async (file: File, restrictions: MediaRestrictions): Promise<File> => {
    const type = await detectFileType(file);

    if (undefined === type) {
        throw new ValidationError('The file type is not supported.');
    }

    assertFileTypeAllowed(type, restrictions);

    if (!RESIZABLE_TYPES.includes(type) && sizeInKiB(file) > 1000) {
        throw new ValidationError('Max file size is 1000KiB.');
    }

    if (sizeInKiB(file) / 1024 > 20) {
        throw new ValidationError('The source image must not be more than 20MiB.');
    }

    return type === file.type ? file : new File([file], file.name, { type, lastModified: file.lastModified });
};

export const uploadMedia = async (
    file: File,
    restrictions: MediaRestrictions,
    owner: UploadOwner,
    onStage?: (stage: UploadStage) => void
): Promise<UploadedMedia> => {
    const accepted = await acceptMedia(file, restrictions);
    let upload = accepted;
    let warning = '';

    try {
        if ('image/heic' === accepted.type) {
            onStage?.('converting');
            upload = await convertHeicToJpeg(accepted);
            warning = 'The image has been automatically converted from HEIC format to JPEG.';

            if (sizeInKiB(upload) > 500) {
                onStage?.('resizing');
                upload = await resizeImage(upload, imageEncoder);
                warning =
                    'The image has been automatically converted from HEIC format to JPEG and resized to be under 500KiB.';
            }
        } else if (RESIZABLE_TYPES.includes(accepted.type) && sizeInKiB(accepted) > 500) {
            onStage?.('resizing');
            upload = await resizeImage(accepted, imageEncoder);
            warning = 'The image has been automatically resized to be under 500KiB.';
        } else if (mayNotBeDelivered(accepted)) {
            warning =
                'The attachment exceeds 500KiB. Please note that it may fail to be delivered, and you may still be charged. We recommend replacing the attachment with something smaller than 500KiB in size to ensure successful delivery and avoid potential charges.';
        }
    } catch (e) {
        console.error(e);
        throw new ValidationError(preparationFailureMessage(e));
    }

    // a superseded owner's request would cancel its successor's under the same key
    if (!owner.isCurrent()) {
        throw new CanceledError();
    }

    onStage?.('uploading');
    const formData = new FormData();
    formData.append('file', upload);
    const response = await messageMediaClient.uploadMedia(Spark.state.currentTeam.id, formData, {
        cancelKey: owner.cancelKey,
    });

    return {
        url: response.data.url,
        contentType: upload.type,
        warning,
    };
};
